import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Accounts from './pages/Accounts';
import Trades from './pages/Trades';
import Webhook from './pages/Webhook';
import PendingTrades from './pages/PendingTrades';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Login from './pages/Login';
import NotificationIndicator from './components/NotificationIndicator';
import ServiceStatus from './components/ServiceStatus';
import { useNotifications } from './hooks/useNotifications';
import { APP_VERSION } from './config/version';

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('auth_token');
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(undefined);
  const { isConnected } = useNotifications(currentUserId);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    setCurrentUserId(0);
  }, []);

  if (isLoginPage) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />

      {/* Navigation */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex space-x-8">
              <Link to="/" className="inline-flex items-center px-1 pt-1 text-gray-900 font-semibold">
                Trading System
              </Link>
              <Link to="/" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-blue-500 text-gray-600 hover:text-gray-900">
                Dashboard
              </Link>
              <Link to="/users" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-blue-500 text-gray-600 hover:text-gray-900">
                Users
              </Link>
              <Link to="/accounts" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-blue-500 text-gray-600 hover:text-gray-900">
                Accounts
              </Link>
              <Link to="/trades" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-blue-500 text-gray-600 hover:text-gray-900">
                Trades
              </Link>
              <Link to="/pending" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-yellow-500 text-yellow-600 hover:text-yellow-900 font-semibold">
                Pending Approval
              </Link>
              <Link to="/webhook" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-blue-500 text-gray-600 hover:text-gray-900">
                Webhook
              </Link>
              <Link to="/analytics" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-green-500 text-gray-600 hover:text-gray-900">
                Analytics
              </Link>
            </div>

            {/* Settings and Logout buttons (right side) */}
            <div className="flex items-center space-x-2">
              <Link
                to="/settings"
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
              <button
                onClick={() => { localStorage.removeItem('auth_token'); window.location.href = '/login'; }}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:text-red-800 hover:bg-red-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 pb-16">
        <Routes>
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
          <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
          <Route path="/trades" element={<ProtectedRoute><Trades /></ProtectedRoute>} />
          <Route path="/pending" element={<ProtectedRoute><PendingTrades /></ProtectedRoute>} />
          <Route path="/webhook" element={<ProtectedRoute><Webhook /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        </Routes>
      </main>

      {/* Notification Indicator */}
      <NotificationIndicator isConnected={isConnected} />

      {/* Service Status Indicator (only in Electron) */}
      <ServiceStatus />

      {/* Version Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gray-800 text-gray-400 py-2 px-4 text-xs text-center">
        <span>Version {APP_VERSION}</span>
      </footer>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
